import React, { useState, useEffect } from 'react';
import AppLayout from '../../components/AppLayout';
import ProspectingNav from '../../components/prospecting/ProspectingNav';
import { useTheme } from '../../contexts/ThemeContext';
import { Download, CheckCircle, XCircle, Loader2, Monitor, Shield, Brain, Plug } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { num: 1, title: 'Download the extension', desc: 'Click the download button below to get the extension ZIP file.' },
  { num: 2, title: 'Unzip the file', desc: 'Extract the downloaded ZIP to a folder on your computer.' },
  { num: 3, title: 'Open Chrome Extensions', desc: 'Navigate to chrome://extensions/ in your Chrome browser.' },
  { num: 4, title: 'Enable Developer Mode', desc: 'Toggle the "Developer mode" switch in the top-right corner.' },
  { num: 5, title: 'Load the extension', desc: 'Click "Load unpacked" and select the unzipped extension folder.' },
  { num: 6, title: 'Configure', desc: 'Click the extension icon, enter your Backend URL and CRM session token, then click Save & Connect.' },
];

export default function ExtensionInstall() {
  const { isDark } = useTheme();
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [checking, setChecking] = useState(false);

  const checkConnection = async () => {
    setChecking(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/prospecting/session/status`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setConnectionStatus(data.linkedin_session?.status === 'active' ? 'connected' : 'not_connected');
      } else {
        setConnectionStatus('error');
      }
    } catch {
      setConnectionStatus('error');
    }
    finally { setChecking(false); }
  };

  const card = `${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'} border rounded-xl`;

  return (
    <AppLayout title="Extension">
      <div className="space-y-6" data-testid="extension-install">
        <ProspectingNav />

        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Chrome Extension</h1>
            <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Install the browser extension for LinkedIn session management and behavioral learning</p>
          </div>
        </div>

        {/* Connection Status */}
        <div className={`${card} p-5`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Plug className={`w-5 h-5 ${connectionStatus === 'connected' ? 'text-green-400' : isDark ? 'text-gray-500' : 'text-gray-400'}`} />
              <div>
                <h2 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Connection Status</h2>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {connectionStatus === 'connected' && 'Extension is connected and LinkedIn session is active'}
                  {connectionStatus === 'not_connected' && 'Extension connected but no active LinkedIn session'}
                  {connectionStatus === 'error' && 'Unable to verify connection'}
                  {!connectionStatus && 'Click "Test Connection" to check your extension status'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {connectionStatus === 'connected' && <CheckCircle className="w-6 h-6 text-green-400" />}
              {connectionStatus === 'not_connected' && <XCircle className="w-6 h-6 text-yellow-400" />}
              {connectionStatus === 'error' && <XCircle className="w-6 h-6 text-red-400" />}
              <button onClick={checkConnection} disabled={checking} data-testid="test-connection-btn" className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${connectionStatus === 'connected' ? 'bg-green-600 text-white' : 'bg-[#A2FF00] text-black hover:bg-[#8FE600]'}`}>
                {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Monitor className="w-4 h-4" />}
                Test Connection
              </button>
            </div>
          </div>
        </div>

        {/* Download */}
        <div className={`${card} p-5 text-center`}>
          <Download className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
          <h2 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Download Extension</h2>
          <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Self-hosted Chrome extension. Not published on Chrome Web Store for security.</p>
          <a href={`${BACKEND_URL}/api/prospecting/extension/download`} download data-testid="download-extension-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#A2FF00] text-black font-medium hover:bg-[#8FE600] transition-colors">
            <Download className="w-5 h-5" /> Download Extension (ZIP)
          </a>
        </div>

        {/* Installation Steps */}
        <div className={`${card} p-5`}>
          <h2 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Installation Guide</h2>
          <div className="space-y-4">
            {STEPS.map(step => (
              <div key={step.num} className="flex gap-4" data-testid={`install-step-${step.num}`}>
                <div className={`flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0 ${isDark ? 'bg-[#A2FF00]/10 text-[#A2FF00]' : 'bg-green-100 text-green-700'}`}>
                  <span className="text-sm font-bold">{step.num}</span>
                </div>
                <div>
                  <h3 className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{step.title}</h3>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { icon: Shield, title: 'Session Management', desc: 'Securely captures LinkedIn session cookies for API access without sharing your password.' },
            { icon: Brain, title: 'Behavioral Learning', desc: 'Learns your natural LinkedIn browsing patterns during Training Mode (7-14 days recommended).' },
            { icon: Monitor, title: 'Action Execution', desc: 'Executes LinkedIn API calls from your browser IP, preventing server-side detection.' },
          ].map((f, i) => (
            <div key={i} className={`${card} p-5`}>
              <f.icon className={`w-8 h-8 mb-3 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
              <h3 className={`font-medium mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{f.title}</h3>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </AppLayout>
  );
}
